function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const defineCustomElementLazy = (name, elementClass) => {
  const registerElement = () => {
    if (customElements.get(name)) {
      return;
    }

    customElements.define(name, elementClass);
  };

  if (customElements.get(name)) {
    return;
  }

  if ('requestIdleCallback' in window) {
    requestIdleCallback(registerElement);
  } else {
    setTimeout(registerElement, 0);
  }
};

class SectionId {
  static #separator = '__';

  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');

    HTMLUpdateUtility.setInnerHTML(
      newNodeWrapper,
      newContent.outerHTML
    );

    const newNode = newNodeWrapper.firstChild;

    const uniqueKey = Date.now();

    oldNode
      .querySelectorAll('[id], [form]')
      .forEach((element) => {
        element.id &&
          (element.id = `${element.id}-${uniqueKey}`);

        element.form &&
          element.setAttribute(
            'form',
            `${element.form.getAttribute('id')}-${uniqueKey}`
          );
      });

    oldNode.parentNode.insertBefore(
      newNode,
      oldNode
    );

    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach(
      (callback) => callback(newNode)
    );

    setTimeout(
      () => oldNode.remove(),
      500
    );
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;

    element
      .querySelectorAll('script')
      .forEach((oldScriptTag) => {
        const newScriptTag =
          document.createElement('script');

        Array.from(
          oldScriptTag.attributes
        ).forEach((attribute) => {
          newScriptTag.setAttribute(
            attribute.name,
            attribute.value
          );
        });

        newScriptTag.appendChild(
          document.createTextNode(
            oldScriptTag.innerHTML
          )
        );

        oldScriptTag.parentNode.replaceChild(
          newScriptTag,
          oldScriptTag
        );
      });
  }
}

window.addEventListener('load', () => {
  document
    .querySelectorAll(
      '[id^="Details-"] summary'
    )
    .forEach((summary) => {
      summary.setAttribute(
        'role',
        'button'
      );

      summary.setAttribute(
        'aria-expanded',
        summary.parentNode.hasAttribute('open')
      );

      if (
        summary.nextElementSibling.getAttribute(
          'id'
        )
      ) {
        summary.setAttribute(
          'aria-controls',
          summary.nextElementSibling.id
        );
      }

      summary.addEventListener(
        'click',
        (event) => {
          event.currentTarget.setAttribute(
            'aria-expanded',
            !event.currentTarget
              .closest('details')
              .hasAttribute('open')
          );
        }
      );

      if (
        summary.closest(
          'header-drawer, menu-drawer'
        )
      ) {
        return;
      }

      summary.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    });
});

const trapFocusHandlers = {};

function trapFocus(
  container,
  elementToFocus = container
) {
  const elements =
    getFocusableElements(container);

  const first = elements[0];
  const last =
    elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (
    event
  ) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    ) {
      return;
    }

    document.addEventListener(
      'keydown',
      trapFocusHandlers.keydown
    );
  };

  trapFocusHandlers.focusout =
    function () {
      document.removeEventListener(
        'keydown',
        trapFocusHandlers.keydown
      );
    };

  trapFocusHandlers.keydown =
    function (event) {
      if (
        event.code.toUpperCase() !==
        'TAB'
      ) {
        return;
      }

      if (
        event.target === last &&
        !event.shiftKey
      ) {
        event.preventDefault();
        first.focus();
      }

      if (
        (
          event.target === container ||
          event.target === first
        ) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };

  document.addEventListener(
    'focusout',
    trapFocusHandlers.focusout
  );

  document.addEventListener(
    'focusin',
    trapFocusHandlers.focusin
  );

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    [
      'search',
      'text',
      'email',
      'url',
    ].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(
      0,
      elementToFocus.value.length
    );
  }
}

try {
  document.querySelector(
    ':focus-visible'
  );
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];

  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener(
    'keydown',
    (event) => {
      if (
        navKeys.includes(
          event.code.toUpperCase()
        )
      ) {
        mouseClick = false;
      }
    }
  );

  window.addEventListener(
    'mousedown',
    () => {
      mouseClick = true;
    }
  );

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) {
        currentFocusedElement.classList.remove(
          'focused'
        );
      }

      if (mouseClick) {
        return;
      }

      currentFocusedElement =
        document.activeElement;

      currentFocusedElement.classList.add(
        'focused'
      );
    },
    true
  );
}

function pauseAllMedia() {
  document
    .querySelectorAll('.js-youtube')
    .forEach((video) => {
      video.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        'https://www.youtube.com'
      );
    });

  document
    .querySelectorAll('.js-vimeo')
    .forEach((video) => {
      video.contentWindow?.postMessage(
        '{"method":"pause"}',
        'https://player.vimeo.com'
      );
    });

  document
    .querySelectorAll('video')
    .forEach((video) => {
      video.pause();
    });

  document
    .querySelectorAll(
      'product-model'
    )
    .forEach((model) => {
      if (model.modelViewerUI) {
        model.modelViewerUI.pause();
      }
    });
}

function removeTrapFocus(
  elementToFocus = null
) {
  document.removeEventListener(
    'focusin',
    trapFocusHandlers.focusin
  );

  document.removeEventListener(
    'focusout',
    trapFocusHandlers.focusout
  );

  document.removeEventListener(
    'keydown',
    trapFocusHandlers.keydown
  );

  if (elementToFocus) {
    elementToFocus.focus();
  }
}

function onKeyUpEscape(event) {
  if (
    event.code.toUpperCase() !==
    'ESCAPE'
  ) {
    return;
  }

  const openDetailsElement =
    event.target.closest(
      'details[open]'
    );

  if (!openDetailsElement) {
    return;
  }

  const summaryElement =
    openDetailsElement.querySelector(
      'summary'
    );

  openDetailsElement.removeAttribute(
    'open'
  );

  summaryElement.setAttribute(
    'aria-expanded',
    false
  );

  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input =
      this.querySelector('input');

    this.buttons =
      Array.from(
        this.querySelectorAll(
          'button'
        )
      );

    this.quantityUpdateUnsubscriber =
      undefined;

    this.boundOnInputChange =
      this.onInputChange.bind(this);

    this.boundOnButtonClick =
      this.onButtonClick.bind(this);

    /*
     * Avoid breaking the entire global.js
     * if incomplete quantity markup exists.
     */
    if (!this.input) {
      return;
    }

    this.input.addEventListener(
      'change',
      this.boundOnInputChange
    );

    this.buttons.forEach(
      (button) => {
        button.addEventListener(
          'click',
          this.boundOnButtonClick
        );
      }
    );
  }

  connectedCallback() {
    if (!this.input) {
      return;
    }

    this.validateQtyRules();

    /*
     * Prevent duplicate subscriptions if
     * this custom element is reconnected.
     */
    this.quantityUpdateUnsubscriber?.();

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !==
        'undefined' &&
      PUB_SUB_EVENTS.quantityUpdate
    ) {
      this.quantityUpdateUnsubscriber =
        subscribe(
          PUB_SUB_EVENTS.quantityUpdate,
          () => {
            this.validateQtyRules();
          }
        );
    }
  }

  disconnectedCallback() {
    this.quantityUpdateUnsubscriber?.();

    this.quantityUpdateUnsubscriber =
      undefined;
  }

  onInputChange() {
    if (!this.input) {
      return;
    }

    this.normalizeInputValue();

    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();

    if (!this.input) {
      return;
    }

    /*
     * IMPORTANT:
     *
     * Use currentTarget instead of target.
     *
     * target may be an SVG / span inside
     * the quantity button.
     */
    const button =
      event.currentTarget;

    const action =
      button?.name;

    if (
      action !== 'plus' &&
      action !== 'minus'
    ) {
      return;
    }

    const previousValue =
      this.getNumericValue(
        this.input.value,
        0
      );

    const min =
      this.getMinimum();

    const max =
      this.getMaximum();

    const step =
      this.getStep();

    let newValue =
      previousValue;

    if (action === 'plus') {
      /*
       * Shopify quantity rules can specify
       * a minimum larger than the normal step.
       *
       * Example:
       *
       * min = 6
       * step = 2
       * current = 0
       *
       * First + must go to 6, not 2.
       */
      if (
        previousValue < min
      ) {
        newValue = min;
      } else {
        newValue =
          previousValue + step;
      }

      if (
        max !== null
      ) {
        newValue =
          Math.min(
            newValue,
            max
          );
      }
    }

    if (action === 'minus') {
      newValue =
        previousValue - step;

      /*
       * Never allow the button to go
       * below Shopify's minimum.
       */
      newValue =
        Math.max(
          newValue,
          min
        );
    }

    /*
     * Align quantity with Shopify's
     * min / step rules.
     */
    newValue =
      this.alignToStep(
        newValue,
        min,
        step
      );

    if (
      max !== null
    ) {
      newValue =
        Math.min(
          newValue,
          max
        );
    }

    newValue =
      Math.max(
        newValue,
        min
      );

    if (
      newValue ===
      previousValue
    ) {
      this.validateQtyRules();
      return;
    }

    this.input.value =
      String(newValue);

    /*
     * Dispatch change only AFTER the final
     * quantity value has been established.
     *
     * This ensures:
     *
     * PricePerItem
     * ProductInfo
     * Quantity rules
     * App integrations
     *
     * all receive the correct value.
     */
    this.input.dispatchEvent(
      new Event(
        'change',
        {
          bubbles: true,
        }
      )
    );
  }

  normalizeInputValue() {
    if (!this.input) {
      return;
    }

    const min =
      this.getMinimum();

    const max =
      this.getMaximum();

    const step =
      this.getStep();

    let value =
      this.getNumericValue(
        this.input.value,
        min
      );

    value =
      Math.max(
        value,
        min
      );

    if (
      max !== null
    ) {
      value =
        Math.min(
          value,
          max
        );
    }

    value =
      this.alignToStep(
        value,
        min,
        step
      );

    if (
      max !== null
    ) {
      value =
        Math.min(
          value,
          max
        );
    }

    this.input.value =
      String(value);
  }

  validateQtyRules() {
    if (!this.input) {
      return;
    }

    const value =
      this.getNumericValue(
        this.input.value,
        this.getMinimum()
      );

    const min =
      this.getMinimum();

    const max =
      this.getMaximum();

    const buttonMinus =
      this.querySelector(
        ".quantity__button[name='minus']"
      );

    const buttonPlus =
      this.querySelector(
        ".quantity__button[name='plus']"
      );

    const disableMinus =
      value <= min;

    const disablePlus =
      max !== null &&
      value >= max;

    /*
     * Keep Dawn's CSS class behaviour.
     */
    buttonMinus?.classList.toggle(
      'disabled',
      disableMinus
    );

    buttonPlus?.classList.toggle(
      'disabled',
      disablePlus
    );

    /*
     * Also expose the disabled state
     * correctly for accessibility.
     *
     * We use aria-disabled rather than
     * native disabled to preserve any
     * existing theme interaction behaviour.
     */
    buttonMinus?.setAttribute(
      'aria-disabled',
      String(disableMinus)
    );

    buttonPlus?.setAttribute(
      'aria-disabled',
      String(disablePlus)
    );
  }

  getMinimum() {
    if (!this.input) {
      return 0;
    }

    const min =
      this.getNumericValue(
        this.input.min ||
          this.input.dataset.min,
        0
      );

    return Math.max(
      min,
      0
    );
  }

  getMaximum() {
    if (!this.input) {
      return null;
    }

    const rawMax =
      this.input.max ||
      this.input.dataset.max;

    if (
      rawMax === undefined ||
      rawMax === null ||
      rawMax === ''
    ) {
      return null;
    }

    const max =
      Number.parseInt(
        rawMax,
        10
      );

    return Number.isFinite(max)
      ? max
      : null;
  }

  getStep() {
    if (!this.input) {
      return 1;
    }

    const step =
      this.getNumericValue(
        this.input.step,
        1
      );

    return step > 0
      ? step
      : 1;
  }

  getNumericValue(
    value,
    fallback = 0
  ) {
    const number =
      Number.parseInt(
        value,
        10
      );

    return Number.isFinite(
      number
    )
      ? number
      : fallback;
  }

  alignToStep(
    value,
    min,
    step
  ) {
    if (
      step <= 1
    ) {
      return value;
    }

    if (
      value <= min
    ) {
      return min;
    }

    const difference =
      value - min;

    const steps =
      Math.round(
        difference / step
      );

    return (
      min +
      steps * step
    );
  }
}

if (
  !customElements.get(
    'quantity-input'
  )
) {
  customElements.define(
    'quantity-input',
    QuantityInput
  );
}

function debounce(fn, wait) {
  let t;

  return (...args) => {
    clearTimeout(t);

    t = setTimeout(
      () => fn.apply(this, args),
      wait
    );
  };
}

function throttle(fn, delay) {
  let lastCall = 0;

  return function (...args) {
    const now =
      new Date().getTime();

    if (
      now - lastCall <
      delay
    ) {
      return;
    }

    lastCall = now;

    return fn(...args);
  };
}

function fetchConfig(
  type = 'json'
) {
  return {
    method: 'POST',
    headers: {
      'Content-Type':
        'application/json',
      Accept:
        `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 */

if (
  typeof window.Shopify ===
  'undefined'
) {
  window.Shopify = {};
}

Shopify.bind = function (
  fn,
  scope
) {
  return function () {
    return fn.apply(
      scope,
      arguments
    );
  };
};

Shopify.setSelectorByValue =
  function (
    selector,
    value
  ) {
    for (
      let i = 0;
      i < selector.options.length;
      i++
    ) {
      const option =
        selector.options[i];

      if (
        value == option.value ||
        value == option.innerHTML
      ) {
        selector.selectedIndex = i;

        return i;
      }
    }
  };

Shopify.addListener =
  function (
    target,
    eventName,
    callback
  ) {
    if (
      target.addEventListener
    ) {
      target.addEventListener(
        eventName,
        callback,
        false
      );
    } else {
      target.attachEvent(
        `on${eventName}`,
        callback
      );
    }
  };

Shopify.postLink = function (
  path,
  options
) {
  options = options || {};

  const method =
    options.method || 'post';

  const params =
    options.parameters || {};

  const form =
    document.createElement(
      'form'
    );

  form.setAttribute(
    'method',
    method
  );

  form.setAttribute(
    'action',
    path
  );

  for (const key in params) {
    if (
      !Object.prototype.hasOwnProperty.call(
        params,
        key
      )
    ) {
      continue;
    }

    const hiddenField =
      document.createElement(
        'input'
      );

    hiddenField.setAttribute(
      'type',
      'hidden'
    );

    hiddenField.setAttribute(
      'name',
      key
    );

    hiddenField.setAttribute(
      'value',
      params[key]
    );

    form.appendChild(
      hiddenField
    );
  }

  document.body.appendChild(
    form
  );

  form.submit();

  document.body.removeChild(
    form
  );
};

Shopify.CountryProvinceSelector =
  function (
    countryDomId,
    provinceDomId,
    options
  ) {
    this.countryEl =
      document.getElementById(
        countryDomId
      );

    this.provinceEl =
      document.getElementById(
        provinceDomId
      );

    this.provinceContainer =
      document.getElementById(
        options.hideElement ||
          provinceDomId
      );

    if (
      !this.countryEl ||
      !this.provinceEl
    ) {
      return;
    }

    Shopify.addListener(
      this.countryEl,
      'change',
      Shopify.bind(
        this.countryHandler,
        this
      )
    );

    this.initCountry();
    this.initProvince();
  };

Shopify.CountryProvinceSelector.prototype =
  {
    initCountry:
      function () {
        if (
          !this.countryEl
        ) {
          return;
        }

        const value =
          this.countryEl.getAttribute(
            'data-default'
          );

        Shopify.setSelectorByValue(
          this.countryEl,
          value
        );

        this.countryHandler();
      },

    initProvince:
      function () {
        if (
          !this.provinceEl
        ) {
          return;
        }

        const value =
          this.provinceEl.getAttribute(
            'data-default'
          );

        if (
          value &&
          this.provinceEl
            .options.length >
            0
        ) {
          Shopify.setSelectorByValue(
            this.provinceEl,
            value
          );
        }
      },

    countryHandler:
      function () {
        if (
          !this.countryEl ||
          !this.provinceEl
        ) {
          return;
        }

        const selected =
          this.countryEl.options[
            this.countryEl
              .selectedIndex
          ];

        if (!selected) {
          return;
        }

        const raw =
          selected.getAttribute(
            'data-provinces'
          );

        let provinces = [];

        try {
          provinces =
            raw
              ? JSON.parse(raw)
              : [];
        } catch (error) {
          console.error(
            'Unable to parse province data.',
            error
          );
        }

        this.clearOptions(
          this.provinceEl
        );

        if (
          !provinces ||
          provinces.length === 0
        ) {
          if (
            this.provinceContainer
          ) {
            this.provinceContainer.style.display =
              'none';
          }

          return;
        }

        provinces.forEach(
          (province) => {
            const option =
              document.createElement(
                'option'
              );

            option.value =
              province[0];

            option.textContent =
              province[1];

            this.provinceEl.appendChild(
              option
            );
          }
        );

        if (
          this.provinceContainer
        ) {
          this.provinceContainer.style.display =
            '';
        }
      },

    clearOptions:
      function (selector) {
        while (
          selector.firstChild
        ) {
          selector.removeChild(
            selector.firstChild
          );
        }
      },

    setOptions:
      function (
        selector,
        values
      ) {
        values.forEach(
          (value) => {
            const option =
              document.createElement(
                'option'
              );

            option.value = value;
            option.textContent =
              value;

            selector.appendChild(
              option
            );
          }
        );
      },
  };

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle =
      this.querySelector(
        'details'
      );

    this.addEventListener(
      'keyup',
      this.onKeyUp.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.bindEvents();
  }

  bindEvents() {
    this
      .querySelectorAll(
        'summary'
      )
      .forEach((summary) => {
        if (
          summary.dataset
            .menuDrawerBound ===
          'true'
        ) {
          return;
        }

        summary.addEventListener(
          'click',
          this.onSummaryClick.bind(
            this
          )
        );

        summary.dataset.menuDrawerBound =
          'true';
      });

    this
      .querySelectorAll(
        'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
      )
      .forEach((button) => {
        if (
          button.dataset
            .menuDrawerBound ===
          'true'
        ) {
          return;
        }

        button.addEventListener(
          'click',
          this.onCloseButtonClick.bind(
            this
          )
        );

        button.dataset.menuDrawerBound =
          'true';
      });
  }

  onKeyUp(event) {
    if (
      event.code.toUpperCase() !==
      'ESCAPE'
    ) {
      return;
    }

    const openDetailsElement =
      event.target.closest(
        'details[open]'
      );

    if (!openDetailsElement) {
      return;
    }

    openDetailsElement ===
    this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector(
            'summary'
          )
        )
      : this.closeSubmenu(
          openDetailsElement
        );
  }

  onSummaryClick(event) {
    const summaryElement =
      event.currentTarget;

    const detailsElement =
      summaryElement.parentNode;

    const parentMenuElement =
      detailsElement.closest(
        '.has-submenu'
      );

    const isOpen =
      detailsElement.hasAttribute(
        'open'
      );

    const reducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

    const addTrapFocus =
      () => {
        trapFocus(
          summaryElement
            .nextElementSibling,
          detailsElement.querySelector(
            'button'
          )
        );

        summaryElement
          .nextElementSibling
          .removeEventListener(
            'transitionend',
            addTrapFocus
          );
      };

    if (
      detailsElement ===
      this.mainDetailsToggle
    ) {
      if (isOpen) {
        event.preventDefault();
      }

      isOpen
        ? this.closeMenuDrawer(
            event,
            summaryElement
          )
        : this.openMenuDrawer(
            summaryElement
          );

      if (
        window.matchMedia(
          '(max-width: 990px)'
        ).matches
      ) {
        document.documentElement.style.setProperty(
          '--viewport-height',
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add(
          'menu-opening'
        );

        summaryElement.setAttribute(
          'aria-expanded',
          true
        );

        parentMenuElement?.classList.add(
          'submenu-open'
        );

        if (
          reducedMotion.matches
        ) {
          addTrapFocus();
        } else {
          summaryElement
            .nextElementSibling
            .addEventListener(
              'transitionend',
              addTrapFocus
            );
        }
      }, 100);
    }
  }

  openMenuDrawer(
    summaryElement
  ) {
    setTimeout(() => {
      this.mainDetailsToggle?.classList.add(
        'menu-opening'
      );
    });

    summaryElement.setAttribute(
      'aria-expanded',
      true
    );

    trapFocus(
      this.mainDetailsToggle,
      summaryElement
    );

    document.body.classList.add(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
  }

  closeMenuDrawer(
    event,
    elementToFocus = false
  ) {
    if (
      event === undefined ||
      !this.mainDetailsToggle
    ) {
      return;
    }

    this.mainDetailsToggle.classList.remove(
      'menu-opening'
    );

    this.mainDetailsToggle
      .querySelectorAll(
        'details'
      )
      .forEach((details) => {
        details.removeAttribute(
          'open'
        );

        details.classList.remove(
          'menu-opening'
        );
      });

    this.mainDetailsToggle
      .querySelectorAll(
        '.submenu-open'
      )
      .forEach((submenu) => {
        submenu.classList.remove(
          'submenu-open'
        );
      });

    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );

    removeTrapFocus(
      elementToFocus
    );

    this.closeAnimation(
      this.mainDetailsToggle
    );

    if (
      event instanceof
        KeyboardEvent &&
      elementToFocus
    ) {
      elementToFocus.setAttribute(
        'aria-expanded',
        false
      );
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle?.hasAttribute(
          'open'
        ) &&
        !this.mainDetailsToggle.contains(
          document.activeElement
        )
      ) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(
    event
  ) {
    const detailsElement =
      event.currentTarget.closest(
        'details'
      );

    if (detailsElement) {
      this.closeSubmenu(
        detailsElement
      );
    }
  }

  closeSubmenu(
    detailsElement
  ) {
    if (!detailsElement) {
      return;
    }

    const parentMenuElement =
      detailsElement.closest(
        '.submenu-open'
      );

    parentMenuElement?.classList.remove(
      'submenu-open'
    );

    detailsElement.classList.remove(
      'menu-opening'
    );

    const summary =
      detailsElement.querySelector(
        'summary'
      );

    summary?.setAttribute(
      'aria-expanded',
      false
    );

    removeTrapFocus(summary);

    this.closeAnimation(
      detailsElement
    );
  }

  closeAnimation(
    detailsElement
  ) {
    let animationStart;

    const handleAnimation =
      (time) => {
        if (
          animationStart ===
          undefined
        ) {
          animationStart = time;
        }

        const elapsedTime =
          time - animationStart;

        if (
          elapsedTime < 400
        ) {
          window.requestAnimationFrame(
            handleAnimation
          );

          return;
        }

        detailsElement.removeAttribute(
          'open'
        );

        const parentDetails =
          detailsElement.closest(
            'details[open]'
          );

        if (parentDetails) {
          trapFocus(
            parentDetails,
            detailsElement.querySelector(
              'summary'
            )
          );
        }
      };

    window.requestAnimationFrame(
      handleAnimation
    );
  }
}

if (
  !customElements.get(
    'menu-drawer'
  )
) {
  customElements.define(
    'menu-drawer',
    MenuDrawer
  );
}

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(
    summaryElement
  ) {
    this.header =
      this.header ||
      document.querySelector(
        '.section-header'
      );

    const wrapper =
      this.closest(
        '.header-wrapper'
      );

    this.borderOffset =
      this.borderOffset ||
      (
        wrapper?.classList.contains(
          'header-wrapper--border-bottom'
        )
          ? 1
          : 0
      );

    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${
          parseInt(
            this.header.getBoundingClientRect()
              .bottom -
              this.borderOffset
          )
        }px`
      );

      this.header.classList.add(
        'menu-open'
      );
    }

    setTimeout(() => {
      this.mainDetailsToggle?.classList.add(
        'menu-opening'
      );
    });

    summaryElement.setAttribute(
      'aria-expanded',
      true
    );

    window.addEventListener(
      'resize',
      this.onResize
    );

    trapFocus(
      this.mainDetailsToggle,
      summaryElement
    );

    document.body.classList.add(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
  }

  closeMenuDrawer(
    event,
    elementToFocus
  ) {
    if (!elementToFocus) {
      return;
    }

    super.closeMenuDrawer(
      event,
      elementToFocus
    );

    this.header?.classList.remove(
      'menu-open'
    );

    window.removeEventListener(
      'resize',
      this.onResize
    );
  }

  onResize = () => {
    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${
          parseInt(
            this.header.getBoundingClientRect()
              .bottom -
              this.borderOffset
          )
        }px`
      );
    }

    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );
  };
}

if (
  !customElements.get(
    'header-drawer'
  )
) {
  customElements.define(
    'header-drawer',
    HeaderDrawer
  );
}

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    const closeButton =
      this.querySelector(
        '[id^="ModalClose-"]'
      );

    closeButton?.addEventListener(
      'click',
      this.hide.bind(
        this,
        false
      )
    );

    this.addEventListener(
      'keyup',
      (event) => {
        if (
          event.code.toUpperCase() ===
          'ESCAPE'
        ) {
          this.hide();
        }
      }
    );

    if (
      this.classList.contains(
        'media-modal'
      )
    ) {
      this.addEventListener(
        'pointerup',
        (event) => {
          if (
            event.pointerType ===
              'mouse' &&
            !event.target.closest(
              'deferred-media, product-model'
            )
          ) {
            this.hide();
          }
        }
      );
    } else {
      this.addEventListener(
        'click',
        (event) => {
          if (
            event.target === this
          ) {
            this.hide();
          }
        }
      );
    }
  }

  connectedCallback() {
    if (this.moved) {
      return;
    }

    this.moved = true;

    document.body.appendChild(
      this
    );
  }

  show(opener) {
    this.openedBy = opener;

    const popup =
      this.querySelector(
        '.template-popup'
      );

    document.body.classList.add(
      'overflow-hidden'
    );

    this.setAttribute(
      'open',
      ''
    );

    if (
      popup &&
      typeof popup.loadContent ===
        'function'
    ) {
      popup.loadContent();
    }

    const dialog =
      this.querySelector(
        '[role="dialog"]'
      );

    if (dialog) {
      trapFocus(
        this,
        dialog
      );
    }

    window.pauseAllMedia?.();
  }

  hide() {
    document.body.classList.remove(
      'overflow-hidden'
    );

    document.body.dispatchEvent(
      new CustomEvent(
        'modalClosed'
      )
    );

    this.removeAttribute(
      'open'
    );

    removeTrapFocus(
      this.openedBy
    );

    window.pauseAllMedia?.();
  }
}

if (
  !customElements.get(
    'modal-dialog'
  )
) {
  customElements.define(
    'modal-dialog',
    ModalDialog
  );
}

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const target =
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      );

    if (!target) {
      return;
    }

    const handleIntersection =
      (
        entries,
        observer
      ) => {
        if (
          !entries[0]
            ?.isIntersecting
        ) {
          return;
        }

        observer.unobserve(
          this
        );

        if (
          this.innerHTML.trim() !==
          ''
        ) {
          return;
        }

        const rawProductUrl =
          this.dataset.url;

        if (!rawProductUrl) {
          return;
        }

        let productUrl;

        try {
          productUrl =
            new URL(
              rawProductUrl.split(
                '?'
              )[0],
              window.location.origin
            );
        } catch (error) {
          console.error(
            'Invalid bulk product URL.',
            error
          );

          return;
        }

        if (
          productUrl.origin !==
          window.location.origin
        ) {
          console.error(
            'Blocked cross-origin bulk product request.'
          );

          return;
        }

        productUrl.searchParams.set(
          'section_id',
          'bulk-quick-order-list'
        );

        fetch(
          productUrl.toString(),
          {
            credentials:
              'same-origin',

            headers: {
              Accept:
                'text/html',
            },
          }
        )
          .then(
            (response) => {
              if (
                !response.ok
              ) {
                throw new Error(
                  `Bulk product request failed: ${response.status} ${response.statusText}`
                );
              }

              return response.text();
            }
          )
          .then(
            (
              responseText
            ) => {
              const html =
                new DOMParser().parseFromString(
                  responseText,
                  'text/html'
                );

              const container =
                html.querySelector(
                  '.quick-order-list-container'
                );

              if (
                !container
                  ?.parentNode
              ) {
                throw new Error(
                  'Bulk quick order content was not found.'
                );
              }

              this.innerHTML =
                container.parentNode.innerHTML;
            }
          )
          .catch(
            (error) => {
              console.error(
                error
              );
            }
          );
      };

    new IntersectionObserver(
      handleIntersection.bind(
        this
      )
    ).observe(target);
  }
}

if (
  !customElements.get(
    'bulk-modal'
  )
) {
  customElements.define(
    'bulk-modal',
    BulkModal
  );
}

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button =
      this.querySelector(
        'button'
      );

    if (!button) {
      return;
    }

    button.addEventListener(
      'click',
      () => {
        const selector =
          this.getAttribute(
            'data-modal'
          );

        if (!selector) {
          return;
        }

        const modal =
          document.querySelector(
            selector
          );

        if (
          modal &&
          typeof modal.show ===
            'function'
        ) {
          modal.show(
            button
          );
        }
      }
    );
  }
}

if (
  !customElements.get(
    'modal-opener'
  )
) {
  customElements.define(
    'modal-opener',
    ModalOpener
  );
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const poster =
      this.querySelector(
        '[id^="Deferred-Poster-"]'
      );

    if (!poster) {
      return;
    }

    poster.addEventListener(
      'click',
      this.loadContent.bind(this)
    );
  }

  loadContent(
    focus = true
  ) {
    window.pauseAllMedia?.();

    if (
      this.getAttribute(
        'loaded'
      )
    ) {
      return;
    }

    const template =
      this.querySelector(
        'template'
      );

    if (
      !template?.content
        ?.firstElementChild
    ) {
      return;
    }

    const content =
      document.createElement(
        'div'
      );

    content.appendChild(
      template.content
        .firstElementChild
        .cloneNode(true)
    );

    this.setAttribute(
      'loaded',
      true
    );

    const media =
      content.querySelector(
        'video, model-viewer, iframe'
      );

    if (!media) {
      return;
    }

    const deferredElement =
      this.appendChild(
        media
      );

    if (focus) {
      deferredElement.focus?.();
    }

    if (
      deferredElement.nodeName ===
        'VIDEO' &&
      deferredElement.getAttribute(
        'autoplay'
      )
    ) {
      deferredElement
        .play()
        .catch(() => {});
    }
  }
}

if (
  !customElements.get(
    'deferred-media'
  )
) {
  customElements.define(
    'deferred-media',
    DeferredMedia
  );
}

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider =
      this.querySelector(
        '[id^="Slider-"]'
      );

    this.sliderItems =
      this.querySelectorAll(
        '[id^="Slide-"]'
      );

    this.enableSliderLooping =
      false;

    this.currentPageElement =
      this.querySelector(
        '.slider-counter--current'
      );

    this.pageTotalElement =
      this.querySelector(
        '.slider-counter--total'
      );

    this.prevButton =
      this.querySelector(
        'button[name="previous"]'
      );

    this.nextButton =
      this.querySelector(
        'button[name="next"]'
      );

    this.updateFrame = null;

    if (
      !this.slider ||
      !this.nextButton
    ) {
      return;
    }

    this.initPages();

    const resizeObserver =
      new ResizeObserver(
        () =>
          this.initPages()
      );

    resizeObserver.observe(
      this.slider
    );

    this.slider.addEventListener(
      'scroll',
      () => {
        if (
          this.updateFrame !== null
        ) {
          return;
        }

        this.updateFrame =
          requestAnimationFrame(
            () => {
              this.updateFrame = null;

              this.update();
            }
          );
      },
      {
        passive: true,
      }
    );

    this.prevButton?.addEventListener(
      'click',
      this.onButtonClick.bind(this)
    );

    this.nextButton.addEventListener(
      'click',
      this.onButtonClick.bind(this)
    );
  }

  initPages() {
    this.sliderItemsToShow =
      Array.from(
        this.sliderItems
      ).filter(
        (element) =>
          element.clientWidth > 0
      );

    if (
      this.sliderItemsToShow
        .length <
      2
    ) {
      return;
    }

    this.sliderItemOffset =
      this.sliderItemsToShow[1]
        .offsetLeft -
      this.sliderItemsToShow[0]
        .offsetLeft;

    if (
      !this.sliderItemOffset
    ) {
      return;
    }

    this.slidesPerPage =
      Math.floor(
        (
          this.slider
            .clientWidth -
          this.sliderItemsToShow[0]
            .offsetLeft
        ) /
          this.sliderItemOffset
      );

    this.totalPages =
      this.sliderItemsToShow
        .length -
      this.slidesPerPage +
      1;

    this.update();
  }

  resetPages() {
    this.sliderItems =
      this.querySelectorAll(
        '[id^="Slide-"]'
      );

    this.initPages();
  }

  update() {
    if (
      !this.slider ||
      !this.nextButton ||
      !this.sliderItemOffset ||
      !this.sliderItemsToShow
        ?.length
    ) {
      return;
    }

    /*
     * READ PHASE
     *
     * Read every layout-dependent value
     * before changing the DOM.
     */
    const previousPage =
      this.currentPage;

    const sliderScrollLeft =
      this.slider.scrollLeft;

    const nextPage =
      Math.round(
        sliderScrollLeft /
          this.sliderItemOffset
      ) + 1;

    let firstSlideVisible = false;
    let lastSlideVisible = false;

    if (
      !this.enableSliderLooping
    ) {
      const sliderClientWidth =
        this.slider.clientWidth;

      const firstItem =
        this.sliderItemsToShow[0];

      const lastItem =
        this.sliderItemsToShow[
          this.sliderItemsToShow
            .length - 1
        ];

      const firstItemOffsetLeft =
        firstItem.offsetLeft;

      const firstItemClientWidth =
        firstItem.clientWidth;

      const lastItemOffsetLeft =
        lastItem.offsetLeft;

      const lastItemClientWidth =
        lastItem.clientWidth;

      const lastVisibleSlide =
        sliderClientWidth +
        sliderScrollLeft;

      firstSlideVisible =
        firstItemOffsetLeft +
          firstItemClientWidth <=
          lastVisibleSlide &&
        firstItemOffsetLeft >=
          sliderScrollLeft &&
        sliderScrollLeft === 0;

      lastSlideVisible =
        lastItemOffsetLeft +
          lastItemClientWidth <=
          lastVisibleSlide &&
        lastItemOffsetLeft >=
          sliderScrollLeft;
    }

    /*
     * WRITE PHASE
     *
     * From this point forward we mutate DOM
     * only after the geometry measurements
     * are complete.
     */
    this.currentPage =
      nextPage;

    if (
      this.currentPageElement &&
      this.pageTotalElement
    ) {
      this.currentPageElement.textContent =
        this.currentPage;

      this.pageTotalElement.textContent =
        this.totalPages;
    }

    if (
      this.currentPage !==
      previousPage
    ) {
      this.dispatchEvent(
        new CustomEvent(
          'slideChanged',
          {
            detail: {
              currentPage:
                this.currentPage,

              currentElement:
                this.sliderItemsToShow[
                  this.currentPage -
                    1
                ],
            },
          }
        )
      );
    }

    if (
      this.enableSliderLooping
    ) {
      return;
    }

    if (
      firstSlideVisible
    ) {
      this.prevButton?.setAttribute(
        'disabled',
        'disabled'
      );
    } else {
      this.prevButton?.removeAttribute(
        'disabled'
      );
    }

    if (
      lastSlideVisible
    ) {
      this.nextButton.setAttribute(
        'disabled',
        'disabled'
      );
    } else {
      this.nextButton.removeAttribute(
        'disabled'
      );
    }
  }

  isSlideVisible(
    element,
    offset = 0
  ) {
    if (!element) {
      return false;
    }

    const lastVisibleSlide =
      this.slider.clientWidth +
      this.slider.scrollLeft -
      offset;

    return (
      element.offsetLeft +
        element.clientWidth <=
        lastVisibleSlide &&
      element.offsetLeft >=
        this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const step =
      Number(
        event.currentTarget
          .dataset.step
      ) || 1;

    this.slideScrollPosition =
      event.currentTarget
        .name === 'next'
        ? this.slider.scrollLeft +
          step *
            this.sliderItemOffset
        : this.slider.scrollLeft -
          step *
            this.sliderItemOffset;

    this.setSlidePosition(
      this.slideScrollPosition
    );
  }

  setSlidePosition(
    position
  ) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

if (
  !customElements.get(
    'slider-component'
  )
) {
  customElements.define(
    'slider-component',
    SliderComponent
  );
}

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();

    this.sliderControlWrapper =
      this.querySelector(
        '.slider-buttons'
      );

    this.enableSliderLooping =
      true;

    if (
      !this.sliderControlWrapper ||
      !this.slider
    ) {
      return;
    }

    this.sliderFirstItemNode =
      this.slider.querySelector(
        '.slideshow__slide'
      );

    if (
      this.sliderItemsToShow
        ?.length >
      0
    ) {
      this.currentPage = 1;
    }

    this.announcementBarSlider =
      this.querySelector(
        '.announcement-bar-slider'
      );

    this.announcerBarAnimationDelay =
      this.announcementBarSlider
        ? 250
        : 0;

    this.sliderControlLinksArray =
      Array.from(
        this.sliderControlWrapper.querySelectorAll(
          '.slider-counter__link'
        )
      );

    this.sliderControlLinksArray.forEach(
      (link) =>
        link.addEventListener(
          'click',
          this.linkToSlide.bind(
            this
          )
        )
    );

    this.slider.addEventListener(
      'scroll',
      this.setSlideVisibility.bind(
        this
      )
    );

    this.setSlideVisibility();

    if (
      this.announcementBarSlider
    ) {
      this.announcementBarArrowButtonWasClicked =
        false;

      this.reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        );

      this.reducedMotion.addEventListener(
        'change',
        () => {
          if (
            this.slider.getAttribute(
              'data-autoplay'
            ) === 'true'
          ) {
            this.setAutoPlay();
          }
        }
      );

      [
        this.prevButton,
        this.nextButton,
      ]
        .filter(Boolean)
        .forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              this.announcementBarArrowButtonWasClicked =
                true;
            },
            {
              once: true,
            }
          );
        });
    }

    if (
      this.slider.getAttribute(
        'data-autoplay'
      ) === 'true'
    ) {
      this.setAutoPlay();
    }
  }

  setAutoPlay() {
    this.autoplaySpeed =
      Number(
        this.slider.dataset.speed
      ) * 1000;

    this.addEventListener(
      'mouseover',
      this.focusInHandling.bind(
        this
      )
    );

    this.addEventListener(
      'mouseleave',
      this.focusOutHandling.bind(
        this
      )
    );

    this.addEventListener(
      'focusin',
      this.focusInHandling.bind(
        this
      )
    );

    this.addEventListener(
      'focusout',
      this.focusOutHandling.bind(
        this
      )
    );

    this.reducedMotion =
      this.reducedMotion ||
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

    if (
      this.querySelector(
        '.slideshow__autoplay'
      )
    ) {
      this.sliderAutoplayButton =
        this.querySelector(
          '.slideshow__autoplay'
        );

      this.sliderAutoplayButton.addEventListener(
        'click',
        this.autoPlayToggle.bind(
          this
        )
      );

      this.autoplayButtonIsSetToPlay =
        true;

      this.play();
    } else {
      this.reducedMotion.matches ||
      this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(
      event
    );

    this.wasClicked = true;

    const isFirstSlide =
      this.currentPage === 1;

    const isLastSlide =
      this.currentPage ===
      this.sliderItemsToShow
        ?.length;

    if (
      !isFirstSlide &&
      !isLastSlide
    ) {
      this.applyAnimationToAnnouncementBar(
        event.currentTarget.name
      );

      return;
    }

    if (
      isFirstSlide &&
      event.currentTarget
        .name === 'previous'
    ) {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode
          .clientWidth *
          this.sliderItemsToShow
            .length;
    } else if (
      isLastSlide &&
      event.currentTarget
        .name === 'next'
    ) {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(
      this.slideScrollPosition
    );

    this.applyAnimationToAnnouncementBar(
      event.currentTarget.name
    );
  }

  setSlidePosition(
    position
  ) {
    if (
      this.setPositionTimeout
    ) {
      clearTimeout(
        this.setPositionTimeout
      );
    }

    this.setPositionTimeout =
      setTimeout(() => {
        this.slider.scrollTo({
          left: position,
        });
      }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();

    if (
      !this.slider ||
      !this.prevButton
    ) {
      return;
    }

    this.sliderControlButtons =
      this.querySelectorAll(
        '.slider-counter__link'
      );

    this.prevButton.removeAttribute(
      'disabled'
    );

    if (
      !this.sliderControlButtons
        .length
    ) {
      return;
    }

    this.sliderControlButtons.forEach(
      (link) => {
        link.classList.remove(
          'slider-counter__link--active'
        );

        link.removeAttribute(
          'aria-current'
        );
      }
    );

    const currentButton =
      this.sliderControlButtons[
        this.currentPage - 1
      ];

    if (currentButton) {
      currentButton.classList.add(
        'slider-counter__link--active'
      );

      currentButton.setAttribute(
        'aria-current',
        'true'
      );
    }
  }

  autoPlayToggle() {
    this.togglePlayButtonState(
      this.autoplayButtonIsSetToPlay
    );

    this.autoplayButtonIsSetToPlay
      ? this.pause()
      : this.play();

    this.autoplayButtonIsSetToPlay =
      !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (
      this.sliderAutoplayButton
    ) {
      const focusedOnAutoplayButton =
        event.target ===
          this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(
          event.target
        );

      if (
        !this.autoplayButtonIsSetToPlay ||
        focusedOnAutoplayButton
      ) {
        return;
      }

      this.play();
    } else if (
      !this.reducedMotion
        ?.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (
      this.sliderAutoplayButton
    ) {
      const focusedOnAutoplayButton =
        event.target ===
          this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(
          event.target
        );

      if (
        focusedOnAutoplayButton &&
        this.autoplayButtonIsSetToPlay
      ) {
        this.play();
      } else if (
        this.autoplayButtonIsSetToPlay
      ) {
        this.pause();
      }
    } else if (
      this.announcementBarSlider?.contains(
        event.target
      )
    ) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute(
      'aria-live',
      'off'
    );

    clearInterval(
      this.autoplay
    );

    this.autoplay =
      setInterval(
        this.autoRotateSlides.bind(
          this
        ),
        this.autoplaySpeed
      );
  }

  pause() {
    this.slider.setAttribute(
      'aria-live',
      'polite'
    );

    clearInterval(
      this.autoplay
    );
  }

  togglePlayButtonState(
    pauseAutoplay
  ) {
    if (
      !this.sliderAutoplayButton
    ) {
      return;
    }

    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add(
        'slideshow__autoplay--paused'
      );

      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window
          .accessibilityStrings
          .playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove(
        'slideshow__autoplay--paused'
      );

      this.sliderAutoplayButton.setAttribute(
        'aria-label',
        window
          .accessibilityStrings
          .pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage ===
      this.sliderItems.length
        ? 0
        : this.slider.scrollLeft +
          this.sliderItemOffset;

    this.setSlidePosition(
      slideScrollPosition
    );

    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility() {
    if (
      !this.sliderItemsToShow
    ) {
      return;
    }

    this.sliderItemsToShow.forEach(
      (item, index) => {
        const linkElements =
          item.querySelectorAll(
            'a'
          );

        if (
          index ===
          this.currentPage - 1
        ) {
          linkElements.forEach(
            (link) =>
              link.removeAttribute(
                'tabindex'
              )
          );

          item.setAttribute(
            'aria-hidden',
            'false'
          );

          item.removeAttribute(
            'tabindex'
          );
        } else {
          linkElements.forEach(
            (link) =>
              link.setAttribute(
                'tabindex',
                '-1'
              )
          );

          item.setAttribute(
            'aria-hidden',
            'true'
          );

          item.setAttribute(
            'tabindex',
            '-1'
          );
        }
      }
    );

    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(
    button = 'next'
  ) {
    if (
      !this.announcementBarSlider ||
      !this.sliderItems.length
    ) {
      return;
    }

    const itemsCount =
      this.sliderItems.length;

    const increment =
      button === 'next'
        ? 1
        : -1;

    const currentIndex =
      this.currentPage - 1;

    let nextIndex =
      (
        currentIndex +
        increment
      ) %
      itemsCount;

    if (nextIndex === -1) {
      nextIndex =
        itemsCount - 1;
    }

    const nextSlide =
      this.sliderItems[
        nextIndex
      ];

    const currentSlide =
      this.sliderItems[
        currentIndex
      ];

    if (
      !nextSlide ||
      !currentSlide
    ) {
      return;
    }

    const animationClassIn =
      'announcement-bar-slider--fade-in';

    const animationClassOut =
      'announcement-bar-slider--fade-out';

    const isFirstSlide =
      currentIndex === 0;

    const isLastSlide =
      currentIndex ===
      itemsCount - 1;

    const shouldMoveNext =
      (
        button === 'next' &&
        !isLastSlide
      ) ||
      (
        button === 'previous' &&
        isFirstSlide
      );

    const direction =
      shouldMoveNext
        ? 'next'
        : 'previous';

    currentSlide.classList.add(
      `${animationClassOut}-${direction}`
    );

    nextSlide.classList.add(
      `${animationClassIn}-${direction}`
    );

    setTimeout(() => {
      currentSlide.classList.remove(
        `${animationClassOut}-${direction}`
      );

      nextSlide.classList.remove(
        `${animationClassIn}-${direction}`
      );
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();

    if (
      !this.sliderFirstItemNode
    ) {
      return;
    }

    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode
        .clientWidth *
        (
          this.sliderControlLinksArray.indexOf(
            event.currentTarget
          ) +
          1 -
          this.currentPage
        );

    this.slider.scrollTo({
      left:
        slideScrollPosition,
    });
  }
}

if (
  !customElements.get(
    'slideshow-component'
  )
) {
  customElements.define(
    'slideshow-component',
    SlideshowComponent
  );
}

class SliderVerticalComponent extends HTMLElement {
  constructor() {
    super();

    this.slider =
      this.querySelector(
        '[id^="Slider-"]'
      );

    this.sliderItems =
      this.querySelectorAll(
        '[id^="Slide-"]'
      );

    this.enableSliderLooping =
      false;

    this.currentPageElement =
      this.querySelector(
        '.slider-counter--current'
      );

    this.pageTotalElement =
      this.querySelector(
        '.slider-counter--total'
      );

    this.prevButton =
      this.querySelector(
        'button[name="previous"]'
      );

    this.nextButton =
      this.querySelector(
        'button[name="next"]'
      );

    if (
      !this.slider ||
      !this.nextButton
    ) {
      return;
    }

    this.initPages();

    const resizeObserver =
      new ResizeObserver(
        () =>
          this.initPages()
      );

    resizeObserver.observe(
      this.slider
    );

    this.slider.addEventListener(
      'scroll',
      this.update.bind(this)
    );

    this.prevButton?.addEventListener(
      'click',
      this.onButtonClick.bind(this)
    );

    this.nextButton.addEventListener(
      'click',
      this.onButtonClick.bind(this)
    );
  }

  initPages() {
    this.sliderItemsToShow =
      Array.from(
        this.sliderItems
      ).filter(
        (element) =>
          element.clientHeight >
          0
      );

    if (
      this.sliderItemsToShow
        .length <
      2
    ) {
      return;
    }

    this.sliderItemOffset =
      this.sliderItemsToShow[1]
        .offsetTop -
      this.sliderItemsToShow[0]
        .offsetTop;

    if (
      !this.sliderItemOffset
    ) {
      return;
    }

    this.slidesPerPage =
      Math.floor(
        (
          this.slider
            .clientHeight -
          this.sliderItemsToShow[0]
            .offsetTop
        ) /
          this.sliderItemOffset
      );

    this.totalPages =
      this.sliderItemsToShow
        .length -
      this.slidesPerPage +
      1;

    this.update();
  }

  resetPages() {
    this.sliderItems =
      this.querySelectorAll(
        '[id^="Slide-"]'
      );

    this.initPages();
  }

  update() {
    if (
      !this.slider ||
      !this.nextButton ||
      !this.sliderItemOffset ||
      !this.sliderItemsToShow
        ?.length
    ) {
      return;
    }

    const previousPage =
      this.currentPage;

    this.currentPage =
      Math.round(
        this.slider.scrollTop /
          this.sliderItemOffset
      ) + 1;

    if (
      this.currentPageElement &&
      this.pageTotalElement
    ) {
      this.currentPageElement.textContent =
        this.currentPage;

      this.pageTotalElement.textContent =
        this.totalPages;
    }

    if (
      this.currentPage !==
      previousPage
    ) {
      this.dispatchEvent(
        new CustomEvent(
          'slideChanged',
          {
            detail: {
              currentPage:
                this.currentPage,

              currentElement:
                this.sliderItemsToShow[
                  this.currentPage -
                    1
                ],
            },
          }
        )
      );
    }

    if (
      this.enableSliderLooping
    ) {
      return;
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[0]
      ) &&
      this.slider.scrollTop ===
        0
    ) {
      this.prevButton?.setAttribute(
        'disabled',
        'disabled'
      );
    } else {
      this.prevButton?.removeAttribute(
        'disabled'
      );
    }

    const lastItem =
      this.sliderItemsToShow[
        this.sliderItemsToShow
          .length - 1
      ];

    if (
      this.isSlideVisible(
        lastItem
      )
    ) {
      this.nextButton.setAttribute(
        'disabled',
        'disabled'
      );
    } else {
      this.nextButton.removeAttribute(
        'disabled'
      );
    }
  }

  isSlideVisible(
    element,
    offset = 0
  ) {
    if (!element) {
      return false;
    }

    const lastVisibleSlide =
      this.slider.clientHeight +
      this.slider.scrollTop -
      offset;

    return (
      element.offsetTop +
        element.clientHeight <=
        lastVisibleSlide &&
      element.offsetTop >=
        this.slider.scrollTop
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const step =
      Number(
        event.currentTarget
          .dataset.step
      ) || 1;

    this.slideScrollPosition =
      event.currentTarget
        .name === 'next'
        ? this.slider.scrollTop +
          step *
            this.sliderItemOffset
        : this.slider.scrollTop -
          step *
            this.sliderItemOffset;

    this.setSlidePosition(
      this.slideScrollPosition
    );
  }

  setSlidePosition(
    position
  ) {
    this.slider.scrollTo({
      top: position,
    });
  }
}

if (
  !customElements.get(
    'slider-vertical-component'
  )
) {
  customElements.define(
    'slider-vertical-component',
    SliderVerticalComponent
  );
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener(
      'change',
      (event) => {
        const target =
          this.getInputForEventTarget(
            event.target
          );

        this.updateSelectionMetadata(
          event
        );

        publish(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          {
            data: {
              event,
              target,
              selectedOptionValues:
                this.selectedOptionValues,
            },
          }
        );
      }
    );
  }

  updateSelectionMetadata({
    target,
  }) {
    if (!target) {
      return;
    }

    const {
      value,
      tagName,
    } = target;

    if (
      tagName === 'SELECT' &&
      target.selectedOptions
        .length
    ) {
      const previouslySelected =
        Array.from(
          target.options
        ).find(
          (option) =>
            option.getAttribute(
              'selected'
            )
        );

      previouslySelected?.removeAttribute(
        'selected'
      );

      target.selectedOptions[0].setAttribute(
        'selected',
        'selected'
      );

      const swatchValue =
        target.selectedOptions[0]
          .dataset
          .optionSwatchValue;

      const field =
        target.closest(
          '.product-form__input'
        );

      const selectedDropdownSwatchValue =
        field?.querySelector(
          '[data-selected-value] > .swatch'
        );

      if (
        !selectedDropdownSwatchValue
      ) {
        return;
      }

      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          swatchValue
        );

        selectedDropdownSwatchValue.classList.remove(
          'swatch--unavailable'
        );
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          'unset'
        );

        selectedDropdownSwatchValue.classList.add(
          'swatch--unavailable'
        );
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0]
          .dataset
          .optionSwatchFocalPoint ||
          'unset'
      );
    } else if (
      tagName === 'INPUT' &&
      target.type === 'radio'
    ) {
      const selectedSwatchValue =
        target
          .closest(
            '.product-form__input'
          )
          ?.querySelector(
            '[data-selected-value]'
          );

      if (
        selectedSwatchValue
      ) {
        selectedSwatchValue.textContent =
          value;
      }
    }
  }

  getInputForEventTarget(
    target
  ) {
    if (
      target.tagName ===
      'SELECT'
    ) {
      return target
        .selectedOptions[0];
    }

    return target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll(
        'select option[selected], fieldset input:checked'
      )
    ).map(
      ({ dataset }) =>
        dataset.optionValueId
    );
  }
}

if (
  !customElements.get(
    'variant-selects'
  )
) {
  customElements.define(
    'variant-selects',
    VariantSelects
  );
}

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(
      this.dataset.productId
    );
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  initializeRecommendations(
    productId
  ) {
    if (!productId) {
      return;
    }

    this.observer?.disconnect();

    this.observer =
      new IntersectionObserver(
        (
          entries,
          observer
        ) => {
          if (
            !entries[0]
              ?.isIntersecting
          ) {
            return;
          }

          observer.unobserve(
            this
          );

          this.loadRecommendations(
            productId
          );
        },
        {
          rootMargin:
            '0px 0px 400px 0px',
        }
      );

    this.observer.observe(
      this
    );
  }

  loadRecommendations(
    productId
  ) {
    const rawUrl =
      this.dataset.url;

    if (!rawUrl) {
      return;
    }

    let requestUrl;

    try {
      requestUrl =
        new URL(
          rawUrl,
          window.location.origin
        );
    } catch (error) {
      console.error(
        'Invalid product recommendations URL.',
        error
      );

      return;
    }

    if (
      requestUrl.origin !==
      window.location.origin
    ) {
      console.error(
        'Blocked cross-origin product recommendations request.'
      );

      return;
    }

    requestUrl.searchParams.set(
      'product_id',
      productId
    );

    requestUrl.searchParams.set(
      'section_id',
      this.dataset.sectionId
    );

    fetch(
      requestUrl.toString(),
      {
        credentials:
          'same-origin',

        headers: {
          Accept: 'text/html',
        },
      }
    )
      .then(
        (response) => {
          if (
            !response.ok
          ) {
            throw new Error(
              `Product recommendations request failed: ${response.status} ${response.statusText}`
            );
          }

          return response.text();
        }
      )
      .then((text) => {
        const html =
          document.createElement(
            'div'
          );

        html.innerHTML = text;

        const recommendations =
          html.querySelector(
            'product-recommendations'
          );

        if (
          recommendations
            ?.innerHTML
            .trim()
            .length
        ) {
          this.innerHTML =
            recommendations.innerHTML;
        }

        if (
          !this.querySelector(
            'slideshow-component'
          ) &&
          this.classList.contains(
            'complementary-products'
          )
        ) {
          this.remove();

          return;
        }

        if (
          html.querySelector(
            '.grid__item'
          )
        ) {
          this.classList.add(
            'product-recommendations--loaded'
          );
        }
      })
      .catch(
        (error) => {
          console.error(
            error
          );
        }
      );
  }
}

if (
  !customElements.get(
    'product-recommendations'
  )
) {
  defineCustomElementLazy(
    'product-recommendations',
    ProductRecommendations
  );
}

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon =
      this.querySelector(
        '.icon'
      );
  }

  connectedCallback() {
    document.addEventListener(
      'storefront:signincompleted',
      this.handleStorefrontSignInCompleted.bind(
        this
      )
    );
  }

  handleStorefrontSignInCompleted(
    event
  ) {
    if (
      event?.detail?.avatar
    ) {
      this.icon?.replaceWith(
        event.detail.avatar.cloneNode()
      );
    }
  }
}

if (
  !customElements.get(
    'account-icon'
  )
) {
  customElements.define(
    'account-icon',
    AccountIcon
  );
}

class BulkAdd extends HTMLElement {
  constructor() {
    super();

    this.queue = [];
    this.requestStarted = false;
    this.ids = [];

    this.queueTimer = null;
    this.queueDelay = 250;
  }

  disconnectedCallback() {
    if (this.queueTimer) {
      clearTimeout(this.queueTimer);
      this.queueTimer = null;
    }
  }

  startQueue(id, quantity) {
    const normalizedId = String(
      id ?? ''
    ).trim();

    const normalizedQuantity =
      Number.parseInt(
        quantity,
        10
      );

    if (
      !normalizedId ||
      !Number.isFinite(
        normalizedQuantity
      ) ||
      normalizedQuantity < 0
    ) {
      return;
    }

    /*
     * Keep only the newest quantity
     * for each variant.
     *
     * 1 → 2 → 3
     *
     * should send 3 instead of
     * three separate cart requests.
     */
    this.queue =
      this.queue.filter(
        (item) =>
          String(item.id) !==
          normalizedId
      );

    this.queue.push({
      id: normalizedId,
      quantity: normalizedQuantity,
    });

    this.scheduleQueue();
  }

  scheduleQueue() {
    if (this.queueTimer) {
      clearTimeout(
        this.queueTimer
      );
    }

    this.queueTimer =
      setTimeout(() => {
        this.queueTimer = null;

        this.sendRequest();
      }, this.queueDelay);
  }

  async sendRequest(
    queue = this.queue
  ) {
    if (this.requestStarted) {
      return;
    }

    const batch =
      Array.isArray(queue)
        ? [...queue]
        : [];

    if (!batch.length) {
      return;
    }

    this.requestStarted = true;

    /*
     * Remove only the entries included
     * in this request.
     *
     * New quantity changes can continue
     * entering this.queue meanwhile.
     */
    this.queue =
      this.queue.filter(
        (queueElement) =>
          !batch.includes(
            queueElement
          )
      );

    const items = {};

    batch.forEach(
      (queueItem) => {
        const variantId =
          Number.parseInt(
            queueItem.id,
            10
          );

        const quantity =
          Number.parseInt(
            queueItem.quantity,
            10
          );

        if (
          !Number.isFinite(
            variantId
          ) ||
          !Number.isFinite(
            quantity
          ) ||
          quantity < 0
        ) {
          return;
        }

        items[variantId] =
          quantity;
      }
    );

    if (
      !Object.keys(items).length
    ) {
      this.requestStarted =
        false;

      if (this.queue.length) {
        this.scheduleQueue();
      }

      return;
    }

    const quickBulkElement =
      this.closest(
        'quick-order-list'
      ) ||
      this.closest(
        'quick-add-bulk'
      );

    try {
      if (
        !quickBulkElement ||
        typeof quickBulkElement
          .updateMultipleQty !==
          'function'
      ) {
        throw new Error(
          'Bulk cart update handler was not found.'
        );
      }

      /*
       * Critical fix:
       *
       * Wait until the real Cart API
       * request has completed before
       * allowing another batch.
       */
      await Promise.resolve(
        quickBulkElement
          .updateMultipleQty(
            items
          )
      );
    } catch (error) {
      console.error(
        '[Bulk Add]',
        error
      );
    } finally {
      this.requestStarted =
        false;

      /*
       * Quantity may have changed again
       * while the previous request
       * was running.
       */
      if (this.queue.length) {
        this.scheduleQueue();
      }
    }
  }

  resetQuantityInput(id) {
    const input =
      this.querySelector(
        `#Quantity-${CSS.escape(
          String(id)
        )}`
      );

    if (!input) {
      return;
    }

    const originalValue =
      input.getAttribute(
        'value'
      );

    input.value =
      originalValue !== null
        ? originalValue
        : '0';

    this.isEnterPressed =
      false;
  }

  setValidity(
    event,
    index,
    message
  ) {
    const target =
      event?.target;

    if (!target) {
      return;
    }

    target.setCustomValidity(
      message || ''
    );

    target.reportValidity();

    this.resetQuantityInput(
      index
    );

    target.select?.();
  }

  validateQuantity(event) {
    const target =
      event?.target;

    if (!target) {
      return;
    }

    const inputValue =
      Number.parseInt(
        target.value,
        10
      );

    const index =
      target.dataset.index;

    if (
      !index ||
      !Number.isFinite(
        inputValue
      )
    ) {
      this.setValidity(
        event,
        index,
        window.cartStrings
          ?.error ||
          'Please enter a valid quantity.'
      );

      return;
    }

    const parsedMin =
      Number.parseInt(
        target.dataset.min ??
          target.min,
        10
      );

    const parsedMax =
      target.max
        ? Number.parseInt(
            target.max,
            10
          )
        : null;

    const parsedStep =
      Number.parseInt(
        target.step,
        10
      );

    const min =
      Number.isFinite(
        parsedMin
      )
        ? parsedMin
        : 0;

    const max =
      Number.isFinite(
        parsedMax
      )
        ? parsedMax
        : null;

    const step =
      Number.isFinite(
        parsedStep
      ) &&
      parsedStep > 0
        ? parsedStep
        : 1;

    let message = '';

    if (
      inputValue < min
    ) {
      message =
        window
          .quickOrderListStrings
          ?.min_error
          ?.replace(
            '[min]',
            min
          ) ||
        `Minimum quantity is ${min}.`;
    } else if (
      max !== null &&
      inputValue > max
    ) {
      message =
        window
          .quickOrderListStrings
          ?.max_error
          ?.replace(
            '[max]',
            max
          ) ||
        `Maximum quantity is ${max}.`;
    } else if (
      (inputValue - min) %
        step !==
      0
    ) {
      /*
       * Respect Shopify min + step.
       *
       * Example:
       * min = 5
       * step = 2
       *
       * Valid:
       * 5, 7, 9, 11...
       */
      message =
        window
          .quickOrderListStrings
          ?.step_error
          ?.replace(
            '[step]',
            step
          ) ||
        `Quantity must increase by ${step}.`;
    }

    if (message) {
      this.setValidity(
        event,
        index,
        message
      );

      return;
    }

    target.setCustomValidity(
      ''
    );

    target.reportValidity();

    this.startQueue(
      index,
      inputValue
    );
  }

  getSectionsUrl() {
    const url =
      new URL(
        window.location.pathname,
        window.location.origin
      );

    if (window.pageNumber) {
      url.searchParams.set(
        'page',
        window.pageNumber
      );
    }

    return (
      `${url.pathname}` +
      `${url.search}`
    );
  }

  getSectionInnerHTML(
    html,
    selector
  ) {
    if (
      !html ||
      !selector
    ) {
      return '';
    }

    const documentHtml =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    return (
      documentHtml
        .querySelector(
          selector
        )
        ?.innerHTML || ''
    );
  }
}

if (
  !customElements.get(
    'bulk-add'
  )
) {
  defineCustomElementLazy(
    'bulk-add',
    BulkAdd
  );
}

class GiftFinder extends HTMLElement {
  constructor() {
    super();

    this.submitButton =
      this.querySelector(
        'button'
      );

    this.age =
      this.querySelector(
        '#age'
      );

    this.gender =
      this.querySelector(
        '#gender'
      );

    this.budget =
      this.querySelector(
        '#budget'
      );

    this.keyword =
      this.querySelector(
        '#keyword'
      );
  }

  connectedCallback() {
    this.submitButton?.addEventListener(
      'click',
      this.handleSubmit.bind(
        this
      )
    );
  }

  handleSubmit(event) {
    event?.preventDefault();

    let error = false;

    if (
      !this.budget?.value
    ) {
      this.budget
        ?.closest(
          '.field--v2'
        )
        ?.classList.add(
          'has--error'
        );

      this.querySelector(
        '#budgetError'
      )?.classList.remove(
        'hidden'
      );

      error = true;
    } else {
      this.budget
        .closest(
          '.field--v2'
        )
        ?.classList.remove(
          'has--error'
        );

      this.querySelector(
        '#budgetError'
      )?.classList.add(
        'hidden'
      );
    }

    if (
      !this.keyword?.value
        ?.trim()
    ) {
      this.keyword
        ?.closest(
          '.field--v2'
        )
        ?.classList.add(
          'has--error'
        );

      this.querySelector(
        '#keyWordError'
      )?.classList.remove(
        'hidden'
      );

      error = true;
    } else {
      this.keyword
        .closest(
          '.field--v2'
        )
        ?.classList.remove(
          'has--error'
        );

      this.querySelector(
        '#keyWordError'
      )?.classList.add(
        'hidden'
      );
    }

    if (error) {
      return;
    }

    const url =
      new URL(
        '/search',
        window.location.origin
      );

    url.searchParams.set(
      'q',
      this.keyword.value.trim()
    );

    url.searchParams.set(
      'options-prefix',
      'last'
    );

    url.searchParams.set(
      'filter.v.price.gte',
      '0'
    );

    url.searchParams.set(
      'filter.v.price.lte',
      this.budget.value
    );

    if (
      this.age?.value
    ) {
      url.searchParams.set(
        'filter.p.m.custom.age',
        this.age.value
      );
    }

    if (
      this.gender?.value
    ) {
      url.searchParams.set(
        'filter.p.m.custom.gender',
        this.gender.value
      );
    }

    window.location.href =
      `${url.pathname}${url.search}`;
  }
}

defineCustomElementLazy(
  'gift-finder',
  GiftFinder
);

class BannerWithCharacters extends HTMLElement {
  constructor() {
    super();

    this.submitButtonAll =
      this.querySelectorAll(
        '.collections--item'
      );

    this.allBanners =
      this.querySelectorAll(
        'div[data-media]'
      );

    this.allActions =
      this.querySelectorAll(
        'a[data-action]'
      );
  }

  connectedCallback() {
    this.submitButtonAll.forEach(
      (button) => {
        button.addEventListener(
          'click',
          () => {
            const index =
              button.dataset.index;

            if (!index) {
              return;
            }

            const banner =
              this.querySelector(
                `#banner-collections--media-${CSS.escape(index)}`
              );

            const action =
              this.querySelector(
                `#banner-collections--action-${CSS.escape(index)}`
              );

            this.closePreviousActions();

            banner?.classList.remove(
              'hidden'
            );

            action?.classList.remove(
              'hidden'
            );
          }
        );
      }
    );
  }

  closePreviousActions() {
    this.allBanners.forEach(
      (item) => {
        item.classList.add(
          'hidden'
        );
      }
    );

    this.allActions.forEach(
      (item) => {
        item.classList.add(
          'hidden'
        );
      }
    );
  }
}

defineCustomElementLazy(
  'banner-with-characters',
  BannerWithCharacters
);

window.addEventListener(
  'load',
  () => {
    const passwordIcon =
      document.querySelector(
        '#passwordIcon'
      );

    const confirmPasswordIcon =
      document.querySelector(
        '#confirmPasswordIcon'
      );

    const togglePasswordVisibility =
      (
        icon,
        input
      ) => {
        if (
          !icon ||
          !input
        ) {
          return;
        }

        const showPassword =
          input.type ===
          'password';

        input.type =
          showPassword
            ? 'text'
            : 'password';

        icon.classList.toggle(
          'eye-slash',
          showPassword
        );
      };

    if (passwordIcon) {
      passwordIcon.addEventListener(
        'click',
        () => {
          togglePasswordVisibility(
            passwordIcon,
            document.querySelector(
              '#password'
            )
          );
        }
      );

      passwordIcon.addEventListener(
        'keydown',
        (event) => {
          if (
            event.key ===
              'Enter' ||
            event.key === ' '
          ) {
            event.preventDefault();

            togglePasswordVisibility(
              passwordIcon,
              document.querySelector(
                '#password'
              )
            );
          }
        }
      );
    }

    if (
      confirmPasswordIcon
    ) {
      confirmPasswordIcon.addEventListener(
        'click',
        () => {
          togglePasswordVisibility(
            confirmPasswordIcon,
            document.querySelector(
              '#confirmPassword'
            )
          );
        }
      );

      confirmPasswordIcon.addEventListener(
        'keydown',
        (event) => {
          if (
            event.key ===
              'Enter' ||
            event.key === ' '
          ) {
            event.preventDefault();

            togglePasswordVisibility(
              confirmPasswordIcon,
              document.querySelector(
                '#confirmPassword'
              )
            );
          }
        }
      );
    }

    const password =
      document.querySelector(
        '#password'
      );

    const confirmPassword =
      document.querySelector(
        '#confirmPassword'
      );

    const registerSubmit =
      document.querySelector(
        '#registerSubmit'
      );

    const errorMessagePasswordMatch =
      document.querySelector(
        '#errorMessagePasswordMatch'
      );

    const validatePasswords =
      () => {
        if (
          !password ||
          !confirmPassword ||
          !registerSubmit ||
          !errorMessagePasswordMatch
        ) {
          return;
        }

        const passwordValue =
          password.value;

        const confirmValue =
          confirmPassword.value;

        const bothEmpty =
          passwordValue.length ===
            0 &&
          confirmValue.length ===
            0;

        if (bothEmpty) {
          errorMessagePasswordMatch.textContent =
            '';

          confirmPassword.setCustomValidity(
            ''
          );

          registerSubmit.disabled =
            true;

          return;
        }

        const match =
          passwordValue.length >
            0 &&
          confirmValue.length >
            0 &&
          passwordValue ===
            confirmValue;

        if (match) {
          errorMessagePasswordMatch.textContent =
            '';

          confirmPassword.setCustomValidity(
            ''
          );

          registerSubmit.disabled =
            false;
        } else {
          errorMessagePasswordMatch.textContent =
            'OOPS! Passwords do not match';

          confirmPassword.setCustomValidity(
            'Passwords do not match.'
          );

          registerSubmit.disabled =
            true;
        }
      };

    password?.addEventListener(
      'input',
      validatePasswords
    );

    confirmPassword?.addEventListener(
      'input',
      validatePasswords
    );

    validatePasswords();
  }
);

window.pauseAllMedia =
  pauseAllMedia;